import { Entity, SystemInterface } from '@goodboydigital/odie';

import { Settings } from '../../AppConfig';
import { GroundFactory } from '../entities/GroundEntity';
import { Game } from '../Game';

export interface ChunkInfo
{
    groundStartX: number;
    groundStartY: number;
    groundEndX: number;
    groundEndY: number;
    segments: number;
}

export interface ChunkEntityData
{
    type: string;
    x: number;
    y: number;
}

export interface ChunkSpriteData extends ChunkEntityData
{
    w: number;
    h: number;
    sx: number;
    sy: number;
    r: number;
    img: string;
}

export interface ChunkTriggerData extends ChunkEntityData
{
    w: number;
    h: number;
    r: number;
}

export interface ChunkVertData extends ChunkEntityData
{
    cx: number;
    cy: number;
}

export interface ChunkSegmentData extends ChunkEntityData
{
    img: string;
    thickness: number;
    verts: ChunkVertData[];
}

export interface ChunkShapeData extends ChunkEntityData
{
    img: string;
    pattern: string;
    thickness: number;
    verts: ChunkVertData[];
}

export interface ChunkData
{
    scale: number;
    name: string;
    info: ChunkInfo;
    entities: ChunkEntityData[];
}

export class ChunkSystem implements SystemInterface
{
    public game: Game;

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;
    }

    public getChunk(name: string): ChunkData
    {
        let data = this.game.app.resources.cache[`chunk-${name}.json`];

        // This should be the default way to fetch a chunk, but used as fallback
        // a problem with the order things are happening in startup
        if (!data) data = this.game.app.data.getChunkData(`${name}`);

        // Panic if data is empty somehow
        if (!data)
        {
            // eslint-disable-next-line no-alert
            if (Settings.chunk) window.alert(`CHUNK NOT AVAILABLE: ${name}`);
            throw new Error(`CHUNK NOT AVAILABLE: ${name}`);
        }

        this.scaleChunk(data);
        this.readChunk(data);

        return data;
    }

    private scaleChunk(data: ChunkData, scale = 0.5): void
    {
        if (data.scale) return;
        data.scale = scale;
        for (const entity of data.entities)
        {
            const en = entity as any;

            if (en.x !== undefined) en.x = this.scaleNumber(en.x, scale);
            if (en.y !== undefined) en.y = this.scaleNumber(en.y, scale);
            if (en.w !== undefined) en.w = this.scaleNumber(en.w, scale);
            if (en.h !== undefined) en.h = this.scaleNumber(en.h, scale);
            if (en.thickness !== undefined) en.thickness = this.scaleNumber(en.thickness, scale);
            if (en.verts)
            {
                for (const vert of en.verts)
                {
                    if (vert.x !== undefined) vert.x = this.scaleNumber(vert.x, scale);
                    if (vert.y !== undefined) vert.y = this.scaleNumber(vert.y, scale);
                    if (vert.cx !== undefined) vert.cx = this.scaleNumber(vert.cx, scale);
                    if (vert.cy !== undefined) vert.cy = this.scaleNumber(vert.cy, scale);
                }
            }
        }
    }

    private scaleNumber(v: number, scale: number)
    {
        return Number((v * scale).toPrecision(4));
    }

    private readChunk(data: ChunkData): void
    {
        if (data.info) return;

        const info: ChunkInfo = {
            groundStartX: Number.POSITIVE_INFINITY,
            groundStartY: Number.POSITIVE_INFINITY,
            groundEndX: Number.NEGATIVE_INFINITY,
            groundEndY: Number.NEGATIVE_INFINITY,
            segments: 0,
        };

        for (const entity of data.entities)
        {
            if (GroundFactory.match(entity as any))
            {
                const en = entity as ChunkSegmentData;

                for (const vert of en.verts)
                {
                    if (vert.x < info.groundStartX)
                    {
                        info.groundStartX = vert.x;
                        info.groundStartY = vert.y;
                    }

                    if (vert.x > info.groundEndX)
                    {
                        info.groundEndX = vert.x;
                        info.groundEndY = vert.y;
                    }
                }
            }

            if (entity.type === 'SegmentEntity') info.segments += 1;
        }

        data.info = info;
    }

    public update(): void
    {
        //
    }
}
